import commonConfig, { useAmplifyInit, Auth } from "common";
import React, { createContext, ReactNode, useCallback } from "react";

interface AmplifyContextType {
  isInitialized: boolean;
  error: Error | null;
  signInWithRedirect: (params: any) => Promise<any>;
  signOut: () => Promise<void>;
  currentAuthenticatedUser: () => Promise<any>;
}

export const AmplifyContext = createContext<AmplifyContextType>({
  isInitialized: false,
  error: null,
  signInWithRedirect: async () => {
    throw new Error("AmplifyContext not initialized");
  },
  signOut: async () => {
    throw new Error("AmplifyContext not initialized");
  },
  currentAuthenticatedUser: async () => {
    throw new Error("AmplifyContext not initialized");
  },
});

interface AmplifyProviderProps {
  children: ReactNode;
}

export const AmplifyProvider: React.FC<AmplifyProviderProps> = ({
  children,
}) => {
  const { isInitialized, error, signInWithRedirect } = useAmplifyInit({
    ...commonConfig,
    isWeb: true,
    appSync: {
      authenticationType: "userPool",
    },
  });

  const signOut = useCallback(async () => {
    if (!isInitialized) {
      throw new Error("AmplifyContext not initialized");
    }
    return Auth.signOut();
  }, [isInitialized]);

  const currentAuthenticatedUser = useCallback(async () => {
    if (!isInitialized) {
      throw new Error("AmplifyContext not initialized");
    }
    return Auth.currentAuthenticatedUser();
  }, [isInitialized]);

  if (error) {
    console.error("Error initializing Amplify:", error);
    return (
      <div>
        Error initializing application. Please check the console for details.
      </div>
    );
  }

  if (!isInitialized) {
    return <div>Initializing application...</div>;
  }

  return (
    <AmplifyContext.Provider
      value={{
        isInitialized,
        error,
        signInWithRedirect,
        signOut,
        currentAuthenticatedUser,
      }}
    >
      {children}
    </AmplifyContext.Provider>
  );
};
